import { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Loader2, CheckCircle2, ArrowRight, Image as ImageIcon, X } from "lucide-react";
import axios from "axios";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "./ui/dialog";
import { Input } from "./ui/input";
import { submitFoundingPledge } from "@/lib/foundingDonors";
import { beginExternalNav, finishExternalNav, isFramed } from "@/lib/externalNav";
import { EmSwash } from "./EmSwash";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * FoundingDonorGiveDialog — the "Add your name to the wall" flow.
 *
 * Collects everything a 501(c)(3) tax-deductible receipt requires
 * (name / email / address) plus optional wall-display fields
 * (phone / alma mater / tribute / photo).
 *
 * On submit, POSTs to /api/founding-donors/pledge. The backend returns
 * `pending=true` while Stripe onboarding is unfinished — the dialog
 * shows a warm "we'll email you when Stripe opens" success. Once
 * Stripe is live, backend returns `checkout_url` and we forward there.
 *
 * Photo handling: the file input renders a client-side preview so the
 * donor sees what the wall will look like. Upload happens on submit
 * only if Object Storage is wired (TODO — currently the pledge is
 * saved without the file; we email the donor a secure upload link
 * post-pledge).
 */

const PRESETS = [
  { cents: 2500,   label: "$25"    },
  { cents: 5000,   label: "$50"    },
  { cents: 10000,  label: "$100"   },
  { cents: 25000,  label: "$250"   },
  { cents: 50000,  label: "$500"   },
  { cents: 100000, label: "$1,000" },
];

const US_STATES = [
  "AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA",
  "KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ",
  "NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT",
  "VA","WA","WV","WI","WY","DC","PR",
];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const ZIP_RE = /^\d{5}(-\d{4})?$/;

const emptyForm = {
  amountCents: 10000,
  customAmount: "",
  isRecurring: false,
  fullName: "",
  anonymous: false,
  email: "",
  phone: "",
  street: "",
  city: "",
  state: "",
  zip: "",
  almaMater: "",
  tribute: "",
  photoFilter: "color",
  photoPreview: null,
  photoUrl: null,          // populated after successful upload
  photoUploading: false,
};

export const FoundingDonorGiveDialog = ({ open, onOpenChange }) => {
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null); // { message } | null
  const [serverError, setServerError] = useState("");
  const fileRef = useRef(null);

  // Reset the whole dialog whenever it closes
  useEffect(() => {
    if (!open) {
      const t = setTimeout(() => {
        setForm(emptyForm);
        setErrors({});
        setSubmitting(false);
        setSuccess(null);
        setServerError("");
      }, 300);
      return () => clearTimeout(t);
    }
  }, [open]);

  const amountCents = useMemo(() => {
    if (form.customAmount) {
      const n = parseFloat(form.customAmount);
      return Number.isFinite(n) && n > 0 ? Math.round(n * 100) : 0;
    }
    return form.amountCents;
  }, [form.customAmount, form.amountCents]);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const validate = () => {
    const e = {};
    if (amountCents < 500) e.amount = "Gift must be at least $5.";
    if (!form.fullName.trim()) e.fullName = "Please share the name for your receipt.";
    if (!EMAIL_RE.test(form.email.trim())) e.email = "Enter a valid email.";
    if (!form.street.trim()) e.street = "Street address is required.";
    if (!form.city.trim()) e.city = "City is required.";
    if (!form.state) e.state = "State is required.";
    if (!ZIP_RE.test(form.zip.trim())) e.zip = "Enter a valid ZIP code.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev?.preventDefault?.();
    if (submitting) return;
    if (!validate()) return;
    setSubmitting(true);
    setServerError("");
    const win = beginExternalNav();   // Stripe Checkout can't render inside the preview frame
    try {
      const body = {
        full_name: form.fullName.trim(),
        email: form.email.trim().toLowerCase(),
        street: form.street.trim(),
        city: form.city.trim(),
        state: form.state,
        zip: form.zip.trim(),
        amount_cents: amountCents,
        is_recurring: form.isRecurring,
        phone: form.phone.trim() || null,
        alma_mater: form.almaMater.trim() || null,
        tribute: form.tribute.trim() || null,
        anonymous: form.anonymous,
        photo_url: form.photoUrl,     // set by uploadPhoto() when donor picks one
        photo_filter: form.photoFilter,
      };
      const res = await submitFoundingPledge(body);
      if (res.checkout_url) {
        finishExternalNav(win, res.checkout_url);
        if (isFramed()) setSubmitting(false);
        return;
      }
      if (win && !win.closed) win.close();
      setSuccess({ message: res.message });
    } catch (err) {
      if (win && !win.closed) win.close();
      const detail = err?.response?.data?.detail;
      setServerError(
        typeof detail === "string"
          ? detail
          : "Something didn't send. Try again in a moment."
      );
      setSubmitting(false);
    }
  };

  const onPhotoPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      setErrors((prev) => ({ ...prev, photo: "Photo must be under 5 MB." }));
      return;
    }
    setErrors((prev) => ({ ...prev, photo: undefined }));

    // Show a preview immediately (before upload finishes) so the donor
    // gets visual feedback while the file is in flight.
    const reader = new FileReader();
    reader.onload = () => set({ photoPreview: reader.result });
    reader.readAsDataURL(file);

    // Upload to Object Storage — the returned URL is what gets stored
    // on the wall permanently.
    set({ photoUploading: true, photoUrl: null });
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/founding-donors/photo`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      // Backend returns a RELATIVE URL (/api/files/…); make it absolute so
      // the donor's browser can render it right off the API host.
      const absolute = data.url.startsWith("http")
        ? data.url
        : `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
      set({ photoUrl: absolute, photoUploading: false });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setErrors((prev) => ({
        ...prev,
        photo: typeof detail === "string" ? detail : "Photo upload failed. Try again.",
      }));
      set({ photoPreview: null, photoUploading: false });
    }
  };

  const clearPhoto = () => {
    set({ photoPreview: null, photoUrl: null, photoUploading: false });
    if (fileRef.current) fileRef.current.value = "";
  };

  const dollars = (cents) => `$${(cents / 100).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="founding-donor-give-dialog"
        className="max-h-[92vh] max-w-3xl overflow-y-auto border-border bg-sand p-0 sm:rounded-lg"
      >
        {/* Header */}
        <div className="relative overflow-hidden bg-beige px-8 pb-7 pt-9">
          <span aria-hidden className="pointer-events-none absolute -right-10 -top-14 h-44 w-44 rounded-full bg-yellow/45 blur-3xl" />
          <span aria-hidden className="pointer-events-none absolute -left-8 -bottom-14 h-40 w-40 rounded-full bg-tangerine/25 blur-3xl" />

          <span className="relative inline-flex h-8 items-center rounded-full bg-ink px-3 text-[13px] font-semibold uppercase tracking-[0.22em] text-sand">
            Founding donor gift
          </span>
          <DialogTitle className="relative mt-4 font-serif text-3xl lg:text-[3.5rem] font-normal leading-[1.02] tracking-tight text-ink">
            Add your name to the <EmSwash>wall.</EmSwash>
          </DialogTitle>
          <DialogDescription className="relative mt-3 max-w-lg text-base leading-relaxed text-olive">
            One gift, one line on the founding roll. Add just your name, or make it yours with a tribute and a photo from when you were the student.
          </DialogDescription>
        </div>

        <AnimatePresence mode="wait">
          {success ? (
            <motion.div
              key="success"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.32, ease: [0.22, 1, 0.36, 1] }}
              data-testid="founding-donor-give-success"
              className="px-8 py-10"
            >
              <div className="mx-auto max-w-md text-center">
                <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-grass text-sand">
                  <CheckCircle2 className="h-6 w-6" strokeWidth={2.2} />
                </span>
                <h3 className="mt-5 font-serif text-3xl leading-tight text-ink">
                  Your spot is held.
                </h3>
                <p className="mt-4 text-base leading-relaxed text-olive">
                  {success.message}
                </p>
                <button
                  type="button"
                  onClick={() => onOpenChange(false)}
                  data-testid="founding-donor-give-close"
                  className="mt-7 inline-flex items-center gap-2 rounded-full bg-ink px-6 py-3 text-base font-semibold text-sand transition hover:bg-forest"
                >
                  Back to the wall <ArrowRight className="h-4 w-4" />
                </button>
              </div>
            </motion.div>
          ) : (
            <motion.form
              key="form"
              onSubmit={submit}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
              className="grid gap-6 px-8 pb-8 pt-6 lg:grid-cols-[1fr_18rem]"
            >
              {/* LEFT — the form */}
              <div className="space-y-6">
                {/* Section 1 — amount */}
                <Section number="1" title="Choose your gift">
                  <div className="grid grid-cols-3 gap-2.5">
                    {PRESETS.map((p) => (
                      <button
                        key={p.cents}
                        type="button"
                        data-testid={`founding-amount-${p.cents}`}
                        onClick={() => set({ amountCents: p.cents, customAmount: "" })}
                        className={`rounded-md border px-3 py-2.5 text-base font-semibold transition ${ !form.customAmount && form.amountCents === p.cents ? "border-tangerine bg-tangerine/10 text-tangerine" : "border-border bg-bone text-ink hover:border-ink" }`}
                      >
                        {p.label}
                      </button>
                    ))}
                  </div>
                  <Input
                    data-testid="founding-amount-custom"
                    value={form.customAmount}
                    onChange={(e) => set({ customAmount: e.target.value.replace(/[^0-9.]/g, "") })}
                    placeholder="or a custom amount: $ ____"
                    inputMode="decimal"
                  />
                  <Err msg={errors.amount} />
                  <label className="flex cursor-pointer items-center justify-between rounded-md border border-border bg-bone p-3">
                    <span className="min-w-0">
                      <span className="block text-[13px] font-semibold text-ink">Make it monthly</span>
                      <span className="mt-0.5 block text-[13px] text-olive">A recurring commitment tells us what we can plan for.</span>
                    </span>
                    <MiniToggle
                      testid="founding-monthly-toggle"
                      on={form.isRecurring}
                      onChange={(v) => set({ isRecurring: v })}
                    />
                  </label>
                </Section>

                {/* Section 2 — how you show up */}
                <Section number="2" title="How you show up on the wall">
                  <div>
                    <Input
                      data-testid="founding-fullname"
                      value={form.fullName}
                      onChange={(e) => set({ fullName: e.target.value })}
                      placeholder="Full name (as it should appear on the receipt)"
                    />
                    <Err msg={errors.fullName} />
                  </div>
                  <label className="mt-2 flex items-start gap-2 text-[13px] text-olive">
                    <input
                      type="checkbox"
                      data-testid="founding-anonymous"
                      checked={form.anonymous}
                      onChange={(e) => set({ anonymous: e.target.checked })}
                      className="mt-0.5 h-4 w-4 shrink-0 rounded border-border accent-ink"
                    />
                    <span>
                      Show me as <span className="font-semibold text-ink">Anonymous</span> on the wall instead of my name.
                    </span>
                  </label>
                </Section>

                {/* Section 3 — contact + address (required) */}
                <Section number="3" title="Contact + billing address" required>
                  <div>
                    <Input
                      data-testid="founding-email"
                      type="email"
                      value={form.email}
                      onChange={(e) => set({ email: e.target.value })}
                      placeholder="Email (for your tax receipt, not shown on the wall)"
                    />
                    <Err msg={errors.email} />
                  </div>
                  <div>
                    <Input
                      data-testid="founding-street"
                      value={form.street}
                      onChange={(e) => set({ street: e.target.value })}
                      placeholder="Street address"
                    />
                    <Err msg={errors.street} />
                  </div>
                  <div className="grid grid-cols-[1fr_5rem_6rem] gap-2.5">
                    <div>
                      <Input
                        data-testid="founding-city"
                        value={form.city}
                        onChange={(e) => set({ city: e.target.value })}
                        placeholder="City"
                      />
                      <Err msg={errors.city} />
                    </div>
                    <div>
                      <select
                        data-testid="founding-state"
                        value={form.state}
                        onChange={(e) => set({ state: e.target.value })}
                        className="flex h-10 w-full rounded-md border border-input bg-background px-2 text-base text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      >
                        <option value="">State</option>
                        {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                      <Err msg={errors.state} />
                    </div>
                    <div>
                      <Input
                        data-testid="founding-zip"
                        value={form.zip}
                        onChange={(e) => set({ zip: e.target.value })}
                        placeholder="ZIP"
                        inputMode="numeric"
                      />
                      <Err msg={errors.zip} />
                    </div>
                  </div>
                  <div>
                    <Input
                      data-testid="founding-phone"
                      type="tel"
                      value={form.phone}
                      onChange={(e) => set({ phone: e.target.value })}
                      placeholder="Phone (optional)"
                    />
                  </div>
                </Section>

                {/* Section 4 — the small things that turn a name into a story */}
                <Section number="4" title="Add your story">
                  <p className="text-base leading-relaxed text-olive">
                    A few small things that turn your name on the wall into a story. All optional, but they help future donors see who came before them.
                  </p>

                  <div>
                    <label className="block text-[13px] font-semibold uppercase tracking-[0.18em] text-olive">
                      Your alma mater <span className="lowercase text-olive/70">(your college or graduate school)</span>
                    </label>
                    <Input
                      data-testid="founding-alma-mater"
                      value={form.almaMater}
                      onChange={(e) => set({ almaMater: e.target.value })}
                      placeholder="e.g. Howard University"
                      className="mt-2"
                    />
                  </div>

                  <div>
                    <label className="block text-[13px] font-semibold uppercase tracking-[0.18em] text-olive">
                      A line of support <span className="lowercase text-olive/70">(why does this matter to you? up to 280 chars)</span>
                    </label>
                    <textarea
                      data-testid="founding-tribute"
                      value={form.tribute}
                      onChange={(e) => set({ tribute: e.target.value.slice(0, 280) })}
                      rows={3}
                      placeholder="Even one sentence carries a lot. Maybe someone helped you along your journey, or you want to leave words of encouragement for the students to come."
                      className="mt-2 w-full rounded-md border border-input bg-background px-3 py-2 text-[13px] leading-relaxed text-ink placeholder:text-ink/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    />
                    <div className="mt-1 text-right text-[13px] text-olive/70">
                      {form.tribute.length}/280
                    </div>
                  </div>

                  {/* Photo picker */}
                  <div className="rounded-md border border-dashed border-tangerine/40 bg-tangerine/[0.04] p-4">
                    <p className="font-caveat text-3xl leading-tight text-tangerine" style={{ fontFamily: "'Caveat', cursive" }}>
                      a small ask, if you're willing
                    </p>
                    <p className="mt-1 font-serif text-3xl leading-snug text-ink">
                      Share a favorite photo of yourself in college or grad school.
                    </p>
                    <p className="mt-1 text-[13px] leading-relaxed text-olive">
                      It'll appear on the founding donors wall. A small tribute to the student you once were, and a reminder to today's students that every one of us started where they are.
                    </p>

                    {form.photoPreview ? (
                      <div className="mt-3 flex items-start gap-3">
                        <div className="relative shrink-0">
                          <img
                            src={form.photoPreview}
                            alt="Your photo preview"
                            className={`h-24 w-24 rounded-md object-cover ${form.photoFilter === "bw" ? "grayscale" : ""}`}
                            data-testid="founding-photo-preview"
                          />
                          {form.photoUploading && (
                            <div
                              data-testid="founding-photo-uploading"
                              className="absolute inset-0 flex items-center justify-center rounded-md bg-ink/60"
                            >
                              <Loader2 className="h-5 w-5 animate-spin text-sand" strokeWidth={2.2} />
                            </div>
                          )}
                          <button
                            type="button"
                            onClick={clearPhoto}
                            data-testid="founding-photo-clear"
                            aria-label="Remove photo"
                            className="absolute -right-2 -top-2 inline-flex h-6 w-6 items-center justify-center rounded-full bg-ink text-sand shadow"
                          >
                            <X className="h-3 w-3" strokeWidth={2.4} />
                          </button>
                        </div>
                        <div className="min-w-0 flex-1 space-y-2">
                          <p className="text-[13px] font-semibold uppercase tracking-[0.18em] text-olive">Photo tone</p>
                          <div className="flex gap-2">
                            <FilterPill
                              testid="founding-filter-color"
                              on={form.photoFilter === "color"}
                              onClick={() => set({ photoFilter: "color" })}
                            >
                              Color
                            </FilterPill>
                            <FilterPill
                              testid="founding-filter-bw"
                              on={form.photoFilter === "bw"}
                              onClick={() => set({ photoFilter: "bw" })}
                            >
                              Black &amp; white
                            </FilterPill>
                          </div>
                          <p className="text-[13px] text-olive">
                            Your choice. No forced filter.
                          </p>
                        </div>
                      </div>
                    ) : (
                      <div className="mt-3">
                        <button
                          type="button"
                          data-testid="founding-photo-choose"
                          onClick={() => fileRef.current?.click()}
                          className="inline-flex items-center gap-2 rounded-full bg-ink px-4 py-2 text-[13px] font-semibold text-sand transition hover:bg-forest"
                        >
                          <ImageIcon className="h-3.5 w-3.5" strokeWidth={2} />
                          Choose a photo
                        </button>
                        <input
                          ref={fileRef}
                          type="file"
                          accept="image/*"
                          onChange={onPhotoPick}
                          className="hidden"
                          data-testid="founding-photo-input"
                        />
                        <Err msg={errors.photo} />
                        <p className="mt-2 text-[13px] text-olive/80">
                          Skip if you prefer. Name-only wall listings are just as welcome.
                        </p>
                      </div>
                    )}
                  </div>
                </Section>
              </div>

              {/* RIGHT — running summary */}
              <aside className="lg:sticky lg:top-6 lg:self-start">
                <div className="rounded-lg border border-border bg-bone p-5">
                  <p className="text-[13px] font-semibold uppercase tracking-[0.22em] text-olive">
                    Your founding gift
                  </p>
                  <p className="mt-2 font-serif text-3xl leading-none text-ink" data-testid="founding-summary-amount">
                    {dollars(amountCents || 0)}
                  </p>
                  <p className="mt-1 text-[13px] text-olive">
                    {form.isRecurring ? "recurring monthly" : "single gift"}
                  </p>
                  <ul className="mt-4 space-y-2 text-[13px] text-ink">
                    <SummaryRow>Founding donor status</SummaryRow>
                    <SummaryRow>Wall listing forever</SummaryRow>
                    <SummaryRow>Optional photo &amp; tribute</SummaryRow>
                    <SummaryRow>Tax-deductible receipt</SummaryRow>
                  </ul>
                </div>

                {serverError && (
                  <p role="alert" data-testid="founding-form-error" className="mt-3 rounded-md bg-scarlet/8 px-3 py-2 text-[13px] text-scarlet">
                    {serverError}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={submitting || form.photoUploading}
                  data-testid="founding-submit"
                  className="mt-4 flex w-full items-center justify-center gap-2 rounded-full bg-ink px-6 py-3.5 text-base font-semibold text-sand transition-colors hover:bg-forest disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {submitting ? (
                    <><Loader2 className="h-4 w-4 animate-spin" /> Saving…</>
                  ) : form.photoUploading ? (
                    <><Loader2 className="h-4 w-4 animate-spin" /> Uploading photo…</>
                  ) : (
                    <>Continue to secure checkout <ArrowRight className="h-4 w-4" strokeWidth={2} /></>
                  )}
                </button>
                <p className="mt-3 text-[13px] leading-relaxed text-olive">
                  Stripe handles all payment info. You&rsquo;ll receive a tax-deductible receipt via email. My Rich Aunt is a registered 501(c)(3) nonprofit.
                </p>
              </aside>
            </motion.form>
          )}
        </AnimatePresence>
      </DialogContent>
    </Dialog>
  );
};

// ── Small helpers ─────────────────────────────────────────────────────

const Section = ({ number, title, required, children }) => (
  <section className="space-y-3">
    <div className="flex items-baseline gap-2.5">
      <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-ink text-[13px] font-semibold text-sand">
        {number}
      </span>
      <h4 className="font-serif text-3xl text-ink">{title}</h4>
      {required && <span className="text-[13px] font-semibold uppercase tracking-[0.18em] text-tangerine">required</span>}
    </div>
    <div className="space-y-2.5 pl-9">{children}</div>
  </section>
);

const Err = ({ msg }) => (msg ? <p className="mt-1 text-[13px] text-scarlet">{msg}</p> : null);

const SummaryRow = ({ children }) => (
  <li className="flex items-start gap-2">
    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-grass" strokeWidth={2} />
    <span>{children}</span>
  </li>
);

const MiniToggle = ({ testid, on, onChange }) => (
  <button
    type="button"
    role="switch"
    aria-checked={on}
    onClick={() => onChange(!on)}
    data-testid={testid}
    className={`inline-flex h-6 w-11 items-center rounded-full transition-colors ${on ? "bg-tangerine" : "bg-ink/25"}`}
  >
    <span
      className={`inline-block h-5 w-5 transform rounded-full bg-sand shadow transition-transform ${on ? "translate-x-5" : "translate-x-0.5"}`}
    />
  </button>
);

const FilterPill = ({ testid, on, onClick, children }) => (
  <button
    type="button"
    data-testid={testid}
    onClick={onClick}
    className={`rounded-full px-3 py-1 text-[13px] font-semibold transition ${ on ? "bg-ink text-sand" : "bg-bone text-ink ring-1 ring-inset ring-border hover:ring-ink" }`}
  >
    {children}
  </button>
);
